import { useCallback } from 'react'

import {
  type FeatureFlagsResponse,
  useFeatureFlags,
} from 'features/project/feature-flags'

// The backend's ENHANCED_DIALECT_FLAG; an existing hub flag, default off.
const ENHANCED_DIALECT_FLAG = 'enhanced_dialect'

/** Whether the charts page should query through the dialect routes, and
 *  whether the flags request has settled (so a caller can wait for it). */
export const useIsEnhancedDialect = (): {
  enabled: boolean
  settled: boolean
} => {
  const { data: flagsResponse, isLoading } = useFeatureFlags()
  return {
    enabled:
      (flagsResponse as FeatureFlagsResponse | undefined)?.[
        ENHANCED_DIALECT_FLAG
      ] === true,
    settled: !isLoading,
  }
}

/** Whether the flag is on, once the flags request settles: for a write that
 *  must not guess. A failed request reads as off, as `useIsEnhancedDialect`
 *  reads it. */
export const useWhenEnhancedDialect = (): (() => Promise<boolean>) => {
  const { data, isSuccess, refetch } = useFeatureFlags()
  return useCallback(async () => {
    // A pending request is joined, not repeated; a failed one is retried.
    const flags = isSuccess ? data : (await refetch()).data
    return (
      (flags as FeatureFlagsResponse | undefined)?.[ENHANCED_DIALECT_FLAG] ===
      true
    )
  }, [data, isSuccess, refetch])
}
